import React, { useContext } from "react";
import ProgressContext from "./ProgressContext";
import "./Placement.css";

// CAREER JOURNEY DASHBOARD V1
// Job opportunities are an informational frontend preview.
// Mentor activation and job listings are not implemented yet.

const careerStages = [
  {
    number: "01",
    icon: "code",
    title: "Complete Technical Learning",
    subtitle: "Build your foundation",
    description:
      "Master the technical curriculum from Linux through React.js and GitHub. Learn to build and understand real applications.",
    points: ["Full Stack fundamentals", "React.js and backend development", "GitHub and practical coding"],
  },
  {
    number: "02",
    icon: "project",
    title: "Build Master Projects",
    subtitle: "Turn skills into proof",
    description:
      "Create serious, industry-style projects that demonstrate your engineering ability and strengthen your professional portfolio.",
    points: ["Real-world project development", "Strong GitHub portfolio", "Project reviews and improvements"],
  },
  {
    number: "03",
    icon: "interview",
    title: "Become Interview-Ready",
    subtitle: "Practice with purpose",
    description:
      "Prepare your resume, revise technical concepts, practice explaining your projects and participate in mock interviews.",
    points: ["Resume and portfolio preparation", "Technical and HR interviews", "Mock interviews and feedback"],
  },
  {
    number: "04",
    icon: "mentor",
    title: "Mentor Readiness Review",
    subtitle: "Your gateway to opportunities",
    description:
      "Your mentor will evaluate project quality, interview readiness and professional preparation before enabling job-opportunity access.",
    points: ["Readiness assessment", "Mentor feedback", "Job access after approval"],
  },
];

function StageArt({ type }) {
  return (
    <div className={`career-art career-art-${type}`} aria-hidden="true">
      {type === "code" && (
        <div className="career-art-window">
          <div className="career-art-dots"><i/><i/><i/></div>
          <div className="career-art-code">
            <span>{"<React />"}</span>
            <em>const future =</em>
            <strong>"developer";</strong>
            <em>build(future);</em>
          </div>
        </div>
      )}
      {type === "project" && (
        <div className="career-art-window">
          <div className="career-art-dots"><i/><i/><i/></div>
          <div className="career-art-project">
            <div className="career-art-project-top">
              <span>PROJECT WORKSPACE</span>
              <b>� LIVE</b>
            </div>
            <div className="career-art-project-bars">
              <i/><i/><i/><i/>
            </div>
            <div className="career-art-project-bottom">
              <span>Frontend</span><span>API</span><span>GitHub</span>
            </div>
          </div>
        </div>
      )}
      {type === "interview" && (
        <div className="career-art-interview">
          <div className="career-art-person"><span>01</span></div>
          <div className="career-art-conversation">
            <span>TECHNICAL</span>
            <i/><i/><i/>
            <strong>INTERVIEW</strong>
          </div>
          <div className="career-art-person"><span>02</span></div>
        </div>
      )}
      {type === "mentor" && (
        <div className="career-art-approval">
          <div className="career-art-approval-ring">
            <span></span>
          </div>
          <strong>MENTOR REVIEW</strong>
          <small>Readiness " Guidance " Approval</small>
        </div>
      )}
    </div>
  );
}

function Placement() {
  const { progress } = useContext(ProgressContext);
  const roundedProgress = Number(parseFloat(progress || 0).toFixed(2));
  const remainingProgress = Math.max(85 - roundedProgress, 0).toFixed(2);
  const preparationMilestoneReached = roundedProgress >= 85;

  return (
    <main className="placement-page career-dashboard">

      <section className="career-hero">
        <div className="career-hero-copy">
          <p className="career-eyebrow">YOUR CAREER JOURNEY STARTS HERE</p>
          <h1>
            From Learning Technologies to{" "}
            <span>Real Job Opportunities.</span>
          </h1>
          <p className="career-hero-description">
            Complete your technical learning through GitHub. Build master
            projects. Prepare for interviews with mentor guidance. Then,
            when you are ready and approved, start exploring opportunities
            and applying for suitable jobs.
          </p>
          <div className="career-hero-tags">
            <span>Learn</span>
            <span>Build</span>
            <span>Prepare</span>
            <span>Get Mentor Approval</span>
            <span>Apply</span>
          </div>
        </div>

        <div className="career-hero-visual" aria-hidden="true">
          <div className="career-orbit career-orbit-one"/>
          <div className="career-orbit career-orbit-two"/>
          <div className="career-visual-core">
            <span className="career-visual-core-top">YOUR NEXT CHAPTER</span>
            <strong>CAREER</strong>
            <strong>READY</strong>
            <span className="career-visual-core-bottom">
              SKILLS � PROJECTS � INTERVIEWS � JOBS
            </span>
          </div>
          <div className="career-orbit-label career-orbit-label-one">01 / SKILLS</div>
          <div className="career-orbit-label career-orbit-label-two">02 / PROJECTS</div>
          <div className="career-orbit-label career-orbit-label-three">03 / CAREER</div>
        </div>
      </section>

      <section className="career-status-section">
        <div className="career-progress-panel">
          <div className="career-progress-header">
            <div>
              <p className="career-eyebrow">YOUR CURRENT PROGRESS</p>
              <h2>Your preparation journey</h2>
            </div>
            <strong className="career-progress-value">
              {roundedProgress}%
            </strong>
          </div>

          <div
            className="career-progress-track"
            role="progressbar"
            aria-label="Learning completion"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={Math.min(Math.max(roundedProgress, 0), 100)}
          >
            <div
              className="career-progress-fill"
              style={{
                width: `${Math.min(Math.max(roundedProgress, 0), 100)}%`
              }}
            />
            <span className="career-progress-marker">
              <b>85%</b>
            </span>
          </div>

          <div className="career-progress-labels">
            <span>Current: {roundedProgress}%</span>
            <span>Preparation milestone: 85%</span>
          </div>

          <div className="career-progress-note">
            <span className="career-status-dot"/>
            {preparationMilestoneReached ? (
              <p>
                <strong>Preparation milestone reached.</strong>{" "}
                Continue strengthening your projects and interview skills.
                Job-opportunity access will require a separate mentor review.
              </p>
            ) : (
              <p>
                <strong>{remainingProgress}% remaining</strong> until the
                current 85% preparation milestone. Keep learning and
                building; job access will require mentor approval later.
              </p>
            )}
          </div>
        </div>

        <div className="career-opportunity-panel">
          <div className="career-opportunity-icon" aria-hidden="true">
            <span>�</span>
          </div>
          <p className="career-eyebrow">UPCOMING FEATURE</p>
          <h2>Your Job Opportunities Hub</h2>
          <p>
            In the future, this space will display relevant job openings
            activated for you by your mentor after your readiness review.
          </p>
          <div className="career-feature-status">
            <span className="career-feature-dot"/>
            Not yet active  planned feature
          </div>
          <small>
            No live vacancies or application functionality are available here yet.
          </small>
        </div>
      </section>

      <section className="career-roadmap-section">
        <div className="career-section-heading">
          <p className="career-eyebrow">A CLEAR ROADMAP TO YOUR FIRST JOB</p>
          <h2>Every step prepares you for the next.</h2>
          <p>
            You are not simply completing lectures. You are building the
            technical ability, project portfolio and interview confidence
            needed to compete for real opportunities.
          </p>
        </div>

        <div className="career-stage-grid">
          {careerStages.map((stage) => (
            <article className="career-stage-card" key={stage.number}>
              <div className="career-stage-top">
                <span className="career-stage-number">{stage.number}</span>
                <span className="career-stage-subtitle">{stage.subtitle}</span>
              </div>

              <StageArt type={stage.icon}/>

              <div className="career-stage-content">
                <h3>{stage.title}</h3>
                <p>{stage.description}</p>
                <ul>
                  {stage.points.map((point) => (
                    <li key={point}>
                      <span aria-hidden="true"></span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="career-next-chapter">
        <div className="career-next-chapter-copy">
          <p className="career-eyebrow">AFTER YOUR MENTOR ACTIVATES ACCESS</p>
          <h2>Your skills become applications, interviews and opportunities.</h2>
          <p>
            Once job access is available, the planned experience will help
            you explore suitable roles, review requirements and apply.
            Mentors can guide your preparation, but selection will depend
            on employer requirements and interview performance.
          </p>
          <div className="career-mini-stages">
            <span>Explore opportunities</span>
            <span>Apply to suitable roles</span>
            <span>Attend interviews</span>
            <span>Grow your career</span>
          </div>
        </div>
        <div className="career-next-chapter-card">
          <span className="career-next-chapter-symbol">�</span>
          <span>JOB OPPORTUNITIES</span>
          <strong>Coming in a future update</strong>
          <p>Access will be activated by mentors when the feature is available.</p>
        </div>
      </section>

      <section className="career-ai-section">
        <div className="career-ai-symbol" aria-hidden="true">AI</div>
        <div>
          <p className="career-eyebrow">KEEP GROWING WHILE YOU APPLY</p>
          <h2>Your career journey does not stop at your first job.</h2>
          <p>
            Alongside project development, interviews and job applications,
            continue learning Artificial Intelligence, Machine Learning
            and advanced technologies. Build capabilities that can help
            you progress toward higher-value roles over time.
          </p>
        </div>
      </section>

      <section className="career-ending">
        <p className="career-eyebrow">YOUR FUTURE IS BUILT STEP BY STEP</p>
        <h2>Learn with purpose. Build with confidence. Prepare to get hired.</h2>
        <p>
          Keep making progress. The goal is to become a capable developer
          who is ready to demonstrate skills, perform in interviews and
          pursue real career opportunities.
        </p>
      </section>

    </main>
  );
}

export default Placement;
